import * as THREE from 'three';
import { FikoARAnchor } from './FikoARAnchor';

export class FikoARKontrol {
  enabled = true;

  private kamera: THREE.PerspectiveCamera;
  private domElement: HTMLElement;
  private anchor: FikoARAnchor;
  private markNeedsRender: () => void;

  private _durum: 'bosta' | 'dondur' | 'zum' | 'tasi' = 'bosta';
  private _oncekiPozisyon = new THREE.Vector2();
  private _oncekiMesafe = 0;
  private _hedef = new THREE.Vector3(0, 0, 0);

  private _phi = Math.PI / 2;
  private _theta = 0;
  private _radius = 100;
  private _minRadius = 1;
  private _maxRadius = 500000;

  private _dondurmeMouse = 0.008;
  private _dondurmeTouch = 0.006;
  private _tasiDamping = 0.5;

  // Gyroscope
  private _gyroAktif = false;
  private _gyroBaslatildi = false;
  private _gyroOncekiAlpha = 0;
  private _gyroOncekiBeta = 0;

  private _onPointerDown: (e: PointerEvent) => void;
  private _onPointerMove: (e: PointerEvent) => void;
  private _onPointerUp: (e: PointerEvent) => void;
  private _onWheel: (e: WheelEvent) => void;
  private _onTouchStart: (e: TouchEvent) => void;
  private _onTouchMove: (e: TouchEvent) => void;
  private _onTouchEnd: (e: TouchEvent) => void;
  private _onDeviceOrientation: ((e: DeviceOrientationEvent) => void) | null = null;

  constructor(
    kamera: THREE.PerspectiveCamera,
    domElement: HTMLElement,
    anchor: FikoARAnchor,
    markNeedsRender: () => void
  ) {
    this.kamera = kamera;
    this.domElement = domElement;
    this.anchor = anchor;
    this.markNeedsRender = markNeedsRender;

    this._radius = kamera.position.length() || 100;
    this._hedef.set(0, 0, 0);
    this.sphericalFromCamera();

    this._onPointerDown = this.onPointerDown.bind(this);
    this._onPointerMove = this.onPointerMove.bind(this);
    this._onPointerUp = this.onPointerUp.bind(this);
    this._onWheel = this.onWheel.bind(this);
    this._onTouchStart = this.onTouchStart.bind(this);
    this._onTouchMove = this.onTouchMove.bind(this);
    this._onTouchEnd = this.onTouchEnd.bind(this);

    domElement.addEventListener('pointerdown', this._onPointerDown);
    domElement.addEventListener('pointermove', this._onPointerMove);
    domElement.addEventListener('pointerup', this._onPointerUp);
    domElement.addEventListener('pointerleave', this._onPointerUp);
    domElement.addEventListener('wheel', this._onWheel, { passive: false });
    domElement.addEventListener('touchstart', this._onTouchStart, { passive: false });
    domElement.addEventListener('touchmove', this._onTouchMove, { passive: false });
    domElement.addEventListener('touchend', this._onTouchEnd);
  }

  anchorGuncellendi(): void {}

  async gyroIzinIste(): Promise<boolean> {
    if (typeof DeviceOrientationEvent === 'undefined') return false;

    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      try {
        const izin = await (DeviceOrientationEvent as any).requestPermission();
        if (izin !== 'granted') return false;
      } catch { return false; }
    }

    if (this._onDeviceOrientation) return true;

    this._gyroBaslatildi = false;
    this._onDeviceOrientation = (e: DeviceOrientationEvent) => {
      if (!this.enabled || !this._gyroAktif) return;
      if (e.alpha == null || e.beta == null) return;

      if (!this._gyroBaslatildi) {
        this._gyroOncekiAlpha = e.alpha;
        this._gyroOncekiBeta = e.beta;
        this._gyroBaslatildi = true;
        return;
      }

      let alphaFark = e.alpha - this._gyroOncekiAlpha;
      let betaFark = e.beta - this._gyroOncekiBeta;

      if (alphaFark > 180) alphaFark -= 360;
      if (alphaFark < -180) alphaFark += 360;
      if (betaFark > 180) betaFark -= 360;
      if (betaFark < -180) betaFark += 360;

      if (Math.abs(alphaFark) > 30 || Math.abs(betaFark) > 30) {
        this._gyroOncekiAlpha = e.alpha;
        this._gyroOncekiBeta = e.beta;
        return;
      }

      this._theta -= alphaFark * 0.015;
      this._phi = Math.max(0.1, Math.min(Math.PI - 0.1, this._phi + betaFark * 0.015));

      this._gyroOncekiAlpha = e.alpha;
      this._gyroOncekiBeta = e.beta;

      this.kameraPozisyonuGuncelle();
    };

    window.addEventListener('deviceorientation', this._onDeviceOrientation);
    this._gyroAktif = true;
    return true;
  }

  gyroToggle(): boolean {
    this._gyroAktif = !this._gyroAktif;
    if (this._gyroAktif) {
      this._gyroBaslatildi = false;
    }
    return this._gyroAktif;
  }

  private sphericalFromCamera(): void {
    const offset = new THREE.Vector3().subVectors(this.kamera.position, this._hedef);
    this._radius = offset.length();
    if (this._radius < 0.001) this._radius = 100;
    this._phi = Math.acos(Math.max(-1, Math.min(1, offset.y / this._radius)));
    this._theta = Math.atan2(offset.x, offset.z);
  }

  private kameraPozisyonuGuncelle(): void {
    const sinPhi = Math.sin(this._phi);
    this.kamera.position.set(
      this._hedef.x + this._radius * sinPhi * Math.sin(this._theta),
      this._hedef.y + this._radius * Math.cos(this._phi),
      this._hedef.z + this._radius * sinPhi * Math.cos(this._theta)
    );
    this.kamera.lookAt(this._hedef);
    this.kamera.updateProjectionMatrix();
    this.markNeedsRender();
  }

  // --- Pointer (Mouse) ---

  private onPointerDown(e: PointerEvent): void {
    if (!this.enabled || e.pointerType === 'touch') return;
    if (e.button === 0) {
      this._durum = 'dondur';
    } else if (e.button === 1 || e.button === 2) {
      this._durum = 'tasi';
    }
    this._oncekiPozisyon.set(e.clientX, e.clientY);
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.enabled || this._durum === 'bosta' || e.pointerType === 'touch') return;
    const dx = e.clientX - this._oncekiPozisyon.x;
    const dy = e.clientY - this._oncekiPozisyon.y;
    this._oncekiPozisyon.set(e.clientX, e.clientY);

    if (this._durum === 'dondur') {
      this._theta -= dx * this._dondurmeMouse;
      this._phi = Math.max(0.1, Math.min(Math.PI - 0.1, this._phi + dy * this._dondurmeMouse));
      this.kameraPozisyonuGuncelle();
    } else if (this._durum === 'tasi' && !this.anchor.kilitli) {
      const panFactor = this._radius * this._tasiDamping * 0.001;
      const right = new THREE.Vector3();
      const up = new THREE.Vector3();
      right.setFromMatrixColumn(this.kamera.matrixWorld, 0);
      up.setFromMatrixColumn(this.kamera.matrixWorld, 1);
      this._hedef.addScaledVector(right, -dx * panFactor);
      this._hedef.addScaledVector(up, dy * panFactor);
      this.kameraPozisyonuGuncelle();
    }
  }

  private onPointerUp(e: PointerEvent): void {
    if (e.pointerType === 'touch') return;
    this._durum = 'bosta';
  }

  private onWheel(e: WheelEvent): void {
    if (!this.enabled) return;
    e.preventDefault();
    const factor = e.deltaY > 0 ? 1.1 : 0.9;
    this._radius = Math.max(this._minRadius, Math.min(this._maxRadius, this._radius * factor));
    this.kameraPozisyonuGuncelle();
  }

  // --- Touch (Mobile) ---

  private onTouchStart(e: TouchEvent): void {
    if (!this.enabled) return;
    e.preventDefault();
    if (e.touches.length === 1) {
      this._durum = 'dondur';
      this._oncekiPozisyon.set(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      this._durum = 'zum';
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      this._oncekiMesafe = Math.sqrt(dx * dx + dy * dy);
    }
  }

  private onTouchMove(e: TouchEvent): void {
    if (!this.enabled) return;
    e.preventDefault();
    if (e.touches.length === 1 && this._durum === 'dondur') {
      const dx = e.touches[0].clientX - this._oncekiPozisyon.x;
      const dy = e.touches[0].clientY - this._oncekiPozisyon.y;
      this._oncekiPozisyon.set(e.touches[0].clientX, e.touches[0].clientY);
      this._theta -= dx * this._dondurmeTouch;
      this._phi = Math.max(0.1, Math.min(Math.PI - 0.1, this._phi + dy * this._dondurmeTouch));
      this.kameraPozisyonuGuncelle();
    } else if (e.touches.length === 2 && this._durum === 'zum') {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const mesafe = Math.sqrt(dx * dx + dy * dy);
      if (this._oncekiMesafe > 0) {
        const factor = this._oncekiMesafe / mesafe;
        this._radius = Math.max(this._minRadius, Math.min(this._maxRadius, this._radius * factor));
        this.kameraPozisyonuGuncelle();
      }
      this._oncekiMesafe = mesafe;
    }
  }

  private onTouchEnd(_e: TouchEvent): void {
    this._durum = 'bosta';
    this._oncekiMesafe = 0;
  }

  dispose(): void {
    this.domElement.removeEventListener('pointerdown', this._onPointerDown);
    this.domElement.removeEventListener('pointermove', this._onPointerMove);
    this.domElement.removeEventListener('pointerup', this._onPointerUp);
    this.domElement.removeEventListener('pointerleave', this._onPointerUp);
    this.domElement.removeEventListener('wheel', this._onWheel);
    this.domElement.removeEventListener('touchstart', this._onTouchStart);
    this.domElement.removeEventListener('touchmove', this._onTouchMove);
    this.domElement.removeEventListener('touchend', this._onTouchEnd);
    if (this._onDeviceOrientation) {
      window.removeEventListener('deviceorientation', this._onDeviceOrientation);
    }
  }
}
